/**
 * Display-name helpers.
 *
 * Staff names are entered freeform by admins, so casing is inconsistent —
 * "seen parker", "SEEN PARKER", "Seen parker". We always render as title
 * case ("Seen Parker") so the header looks consistent.
 *
 * We respect common name particles (O', Mc, Mac, van, de, etc.) with a
 * light-touch rule set. Full i18n name formatting is out of scope.
 */

const LOWERCASE_PARTICLES = new Set([
  "van",
  "von",
  "de",
  "del",
  "della",
  "di",
  "da",
  "la",
  "le",
  "du",
  "bin",
  "al",
]);

function titleCaseWord(word: string): string {
  if (!word) return word;
  const lower = word.toLowerCase();

  // Respect mid-name particles like "van", "de" — but only when they're not
  // the first/only word (caller handles position).
  // The caller opts in by passing `isParticle`.

  // Handle O'Brien, D'Angelo
  if (lower.length >= 3 && (lower.startsWith("o'") || lower.startsWith("d'"))) {
    return (
      lower[0].toUpperCase() +
      "'" +
      lower[2].toUpperCase() +
      lower.slice(3)
    );
  }

  // Handle McKenzie, MacArthur
  if (lower.startsWith("mc") && lower.length > 2) {
    return "Mc" + lower[2].toUpperCase() + lower.slice(3);
  }
  if (lower.startsWith("mac") && lower.length > 3) {
    return "Mac" + lower[3].toUpperCase() + lower.slice(4);
  }

  // Hyphenated: Jean-Luc -> Jean-Luc
  if (lower.includes("-")) {
    return lower
      .split("-")
      .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
      .join("-");
  }

  return lower[0].toUpperCase() + lower.slice(1);
}

/**
 * Title-case a person's full name. Multiple whitespace runs collapse to a
 * single space. Returns an empty string if input is nullish/blank.
 */
export function titleCaseName(raw: string | null | undefined): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";

  const parts = trimmed.split(/\s+/);
  return parts
    .map((word, idx) => {
      const lower = word.toLowerCase();
      // Mid-name particles stay lowercase (but never the first word).
      if (idx > 0 && LOWERCASE_PARTICLES.has(lower)) return lower;
      return titleCaseWord(word);
    })
    .join(" ");
}

/**
 * First-name-only title case, for greetings ("Good morning, Seen").
 */
export function titleCaseFirstName(raw: string | null | undefined): string {
  const full = titleCaseName(raw);
  if (!full) return "";
  return full.split(" ")[0];
}
